import { paymentMethodBreakdown } from "@/mocks/admin/payments";

// Hex equivalents of Tailwind color classes for SVG fills
const colorHex: Record<string, string> = {
  "bg-ink-900": "#0F172A",
  "bg-brand-500": "#F97316",
  "bg-emerald-500": "#10B981",
  "bg-amber-500": "#F59E0B",
};

// Total monthly volume → distribute across methods by %
const totalMonthly = 148250;

export default function MethodBreakdown() {
  const methods = paymentMethodBreakdown.map((m) => ({
    ...m,
    amount: Math.round((m.value / 100) * totalMonthly),
    txns: Math.round((m.value / 100) * 187), // synthesize tx count
  }));

  // Donut math
  const size = 140;
  const stroke = 18;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  let offset = 0;
  const segments = methods.map((m) => {
    const length = (m.value / 100) * circumference;
    const seg = {
      ...m,
      strokeDasharray: `${length} ${circumference - length}`,
      strokeDashoffset: -offset,
      hex: colorHex[m.color] || "#0F172A",
    };
    offset += length;
    return seg;
  });

  return (
    <div className="bg-white border border-ink-100 rounded-lg p-6 h-full flex flex-col">
      <h3 className="font-display text-lg font-bold text-ink-900 mb-1">
        Payment Methods
      </h3>
      <p className="text-xs text-ink-500">Distribution this month</p>

      {/* Donut */}
      <div className="flex items-center justify-center my-5">
        <div className="relative" style={{ width: size, height: size }}>
          <svg
            width={size}
            height={size}
            viewBox={`0 0 ${size} ${size}`}
            className="-rotate-90"
          >
            {/* Background ring */}
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke="#FAF5EC"
              strokeWidth={stroke}
            />
            {segments.map((s) => (
              <circle
                key={s.label}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={s.hex}
                strokeWidth={stroke}
                strokeDasharray={s.strokeDasharray}
                strokeDashoffset={s.strokeDashoffset}
                strokeLinecap="butt"
              />
            ))}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="text-[10px] font-bold uppercase tracking-wider text-ink-400">
              Total
            </div>
            <div className="font-display text-lg font-bold text-ink-900 tabular-nums leading-tight">
              ${(totalMonthly / 1000).toFixed(1)}k
            </div>
            <div className="text-[10px] text-ink-500">this month</div>
          </div>
        </div>
      </div>

      {/* List */}
      <ul className="space-y-2.5 flex-1">
        {methods.map((m) => (
          <li
            key={m.label}
            className="flex items-center gap-2.5 py-1 group cursor-pointer"
          >
            <span
              className={`w-2.5 h-2.5 rounded-sm shrink-0 ${m.color}`}
            ></span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-bold text-ink-900 truncate">
                  {m.label}
                </span>
                <span className="text-xs font-bold tabular-nums text-ink-900">
                  {m.value}%
                </span>
              </div>
              <div className="flex items-center justify-between gap-2 text-[10px] text-ink-500 tabular-nums">
                <span>${m.amount.toLocaleString()}</span>
                <span>{m.txns} txns</span>
              </div>
            </div>
          </li>
        ))}
      </ul>

      <div className="mt-5 pt-4 border-t border-ink-100">
        <div className="text-[10px] font-bold uppercase tracking-wider text-ink-400 mb-2">
          Quick actions
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold border border-ink-200 text-ink-700 hover:bg-cream-100 cursor-pointer whitespace-nowrap">
            <i className="ri-file-list-3-line"></i>
            Invoice
          </button>
          <button className="inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md text-xs font-semibold border border-ink-200 text-ink-700 hover:bg-cream-100 cursor-pointer whitespace-nowrap">
            <i className="ri-arrow-go-back-line"></i>
            Refund
          </button>
        </div>
      </div>
    </div>
  );
}